import React from 'react';
import { motion } from 'framer-motion';
import { FaEye, FaBullseye, FaUserMd, FaFlask, FaAward, FaHandshake } from 'react-icons/fa';
import './About.css';

const About = () => {
  const values = [
    {
      icon: FaUserMd,
      title: 'Expert Excellence',
      description: 'Our team of seasoned pharmaceutical professionals brings over 15 years of combined experience.'
    },
    {
      icon: FaFlask,
      title: 'Quality Innovation',
      description: 'Partnering with renowned manufacturers to deliver cutting-edge pharmaceutical solutions.'
    },
    {
      icon: FaAward,
      title: 'Highest Standards',
      description: 'Committed to maintaining the highest quality standards in all our products and services.'
    },
    {
      icon: FaHandshake,
      title: 'Patient-Centric',
      description: 'Your health and well-being are at the center of everything we do.'
    }
  ];

  const milestones = [
    { year: '2024', title: 'Company Founded', description: 'Sarvatman Life Science was established with a vision to revolutionize healthcare.' },
    { year: '2024', title: 'Product Launch', description: 'Successfully launched our first range of Neuro and Psychiatry medicines.' },
    { year: '2024', title: 'Quality Partnerships', description: 'Established partnerships with leading pharmaceutical manufacturers.' },
    { year: '2024', title: 'Market Expansion', description: 'Expanding our reach to serve more patients across regions.' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-background">
          <div className="about-hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="about-hero-title">About Sarvatman Lifescience</h1>
            <p className="about-hero-subtitle">
              Pioneering Excellence in Pharmaceutical Healthcare Since 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="about-main section">
        <div className="container">
          <div className="about-content-grid">
            <motion.div
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Our Story</h2>
              <p className="about-description">
                Founded in 2024, Sarvatman Lifescience emerged from a shared vision to transform 
                healthcare through precision medicine. Despite being new to the pharmaceutical 
                industry, our foundation is built on over 15 years of collective experience from 
                seasoned healthcare professionals who understand the critical importance of quality 
                healthcare solutions.
              </p>
              <p className="about-description">
                We specialize in five critical therapeutic areas: Neuropathic Pain, Pain Management, 
                Migraine & Vertigo, Vitamins & Neuropathy, and PPIs (Proton Pump Inhibitors). 
                These specialized fields require deep expertise, unwavering commitment to quality, 
                and a patient-first approach – values that define our company culture and operational philosophy.
              </p>
              <p className="about-description">
                Our strategic partnerships with renowned pharmaceutical manufacturers ensure that 
                every product we deliver meets the highest international standards. We believe 
                that safe, effective, and reliable healthcare solutions are not just products 
                but lifelines that can transform lives and restore hope.
              </p>
            </motion.div>

            <motion.div
              className="about-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="about-image-card">
                <div className="about-stats-grid">
                  <div className="about-stat">
                    <span className="stat-number">15+</span>
                    <span className="stat-label">Years Combined Experience</span>
                  </div>
                  <div className="about-stat">
                    <span className="stat-number">5</span>
                    <span className="stat-label">Therapeutic Areas</span>
                  </div>
                  <div className="about-stat">
                    <span className="stat-number">11</span>
                    <span className="stat-label">Quality Products</span>
                  </div>
                  <div className="about-stat">
                    <span className="stat-number">100%</span>
                    <span className="stat-label">Quality Commitment</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="vision-section section">
        <div className="container">
          <div className="vision-mission-grid">
            <motion.div
              className="vision-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="vm-icon">
                <FaEye />
              </div>
              <h3 className="vm-title">Our Vision</h3>
              <p className="vm-description">
                To be a leading pharmaceutical company recognized globally for excellence in 
                Neurology, Psychiatry, and Pain Management. We envision a world where advanced 
                healthcare solutions are accessible to all, improving quality of life and 
                empowering patients to live healthier, more fulfilling lives.
              </p>
            </motion.div>

            <motion.div
              className="mission-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="vm-icon">
                <FaBullseye />
              </div>
              <h3 className="vm-title">Our Mission</h3>
              <p className="vm-description">
                To deliver safe, effective, and innovative pharmaceutical solutions that address 
                critical healthcare needs. Through strategic partnerships, unwavering quality 
                standards, and patient-centric approach, we strive to make a meaningful difference 
                in the lives of patients and healthcare providers worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide our commitment to excellence in healthcare
            </p>
          </motion.div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                viewport={{ once: true }}
              >
                <div className="value-icon">
                  <value.icon />
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Journey</h2>
            <p className="section-subtitle">
              Key milestones in our commitment to healthcare excellence
            </p>
          </motion.div>

          <div className="timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="timeline-content">
                  <div className="timeline-year">{milestone.year}</div>
                  <h3 className="timeline-title">{milestone.title}</h3>
                  <p className="timeline-description">{milestone.description}</p>
                </div>
                <div className="timeline-marker"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
